import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { FileAttributes, FileAttributesDTO } from "../types/fileAttributes";
/* eslint-disable import/export */
export abstract class GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC implements
  LeoRPC<GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Request, GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Response, GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Response, GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Errors.Errors>>;
}

export namespace GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    fileAttributes: FileAttributesDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDocumentUploadURLForSpocRespondentUnmappingRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly fileAttributes: FileAttributes;
  
  
    constructor(
    projectId: LeoUUID, 
    fileAttributes: FileAttributes
    ) {
      this.projectId = projectId;
      this.fileAttributes = fileAttributes;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const fileAttributes = FileAttributes.fromDTO(getObject(dto, "fileAttributes", InvalidRequestFileAttributesError));
      return new Request(
        projectId,
        fileAttributes
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          fileAttributes: this.fileAttributes.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC(projectId='${this.projectId}',fileAttributes='${this.fileAttributes}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDocumentUploadURLForSpocRespondentUnmappingResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDocumentUploadURLForSpocRespondentUnmapping(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SpocAssignmentInProgress = "SPOC_ASSIGNMENT_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SpocAssignmentInProgressDTO {
      code: string;
    }
    
    export class SpocAssignmentInProgress extends LeoRPCError<RPCError> {
      readonly isSpocAssignmentInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SpocAssignmentInProgress;
        super(code);
      }
    
    
      toDTO(): SpocAssignmentInProgressDTO {
        const dto: SpocAssignmentInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SpocAssignmentInProgress.prototype.toString = function toString(): string {
      return `GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.SPOC_ASSIGNMENT_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | SpocAssignmentInProgress;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | SpocAssignmentInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SpocAssignmentInProgress:
          return new SpocAssignmentInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestFileAttributesError extends GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileAttributesError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSpocAssignmentInProgressError extends GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocAssignmentInProgressError";
  }
}

